import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Camera, Crop as CropIcon, Trash2, UserRound } from 'lucide-react'
import { useRef, useState } from 'react'
import type { Crop } from '../../shared/crop'
import { CropPhoto } from '@/components/CropPhoto'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { api, type Me } from '@/lib/api'
import { PHOTO_TYPES, saveCrop } from '@/lib/photos'
import { portraitUrl, uploadProfilePhoto } from '@/lib/tree'
import { cn } from '@/lib/utils'

// Your profile photo: your face, tapped to add or change it, cropped square as it's added. It's on your account, so
// it's there before any circle or tree, and shows wherever you do. `edit` adds Crop and Remove under it, for My profile.
export function ProfilePhoto({
  me,
  edit = false,
  fill = false,
  className,
}: {
  me: Me
  edit?: boolean
  // Filling the frame it's put in (a ring), rather than sized on its own.
  fill?: boolean
  className?: string
}) {
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [recrop, setRecrop] = useState<{ id: string; crop: Crop | null } | null>(null)
  // The new face in place at once, so a step in progress isn't redrawn; then everywhere else catches up.
  async function changed(portrait: string | null) {
    queryClient.setQueryData<Me>(['me'], (old) => old && { ...old, portrait, photo: portrait !== null })
    await queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'me' })
  }
  const open = useMutation({
    mutationFn: () => api<{ id: string; crop: Crop | null }>('/me/photo'),
    onSuccess: setRecrop,
  })
  const remove = useMutation({
    mutationFn: () => api('/me/photo', { method: 'DELETE' }),
    // Back to a tree portrait where there is one, so everything is fetched again.
    onSuccess: () => queryClient.invalidateQueries(),
  })
  const label = me.photo ? 'Change your profile photo' : 'Add a profile photo'
  return (
    <div className={cn('flex flex-col items-center gap-3', fill && 'size-full')}>
      <input
        ref={input}
        type="file"
        accept={PHOTO_TYPES}
        className="sr-only"
        tabIndex={-1}
        aria-label="Profile photo"
        onChange={(e) => {
          const chosen = e.target.files?.[0]
          if (chosen) setFile(chosen)
          e.target.value = ''
        }}
      />
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => input.current?.click()}
        className={cn(
          'group flex flex-col items-center gap-3 rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
          fill && 'size-full',
        )}
      >
        <span className={cn('relative', fill ? 'size-full' : 'size-32', className)}>
          <span className="grid size-full place-items-center overflow-hidden rounded-full bg-secondary ring-1 ring-foreground/5 transition-transform duration-200 group-active:scale-[0.97]">
            {me.portrait ? (
              <img
                src={portraitUrl(me.portrait, 'display')}
                alt=""
                decoding="async"
                className="size-full object-cover"
              />
            ) : (
              <UserRound className="size-[44%] text-muted-foreground/60" strokeWidth={1.25} />
            )}
          </span>
          <span
            aria-hidden
            className="absolute right-[4%] bottom-[4%] grid size-9 place-items-center rounded-full bg-background text-foreground shadow-md ring-1 ring-foreground/10 transition-colors group-hover:bg-secondary"
          >
            <Camera className="size-4" strokeWidth={1.75} />
          </span>
        </span>
        {!fill && <span className="text-sub font-medium text-brand">{me.photo ? 'Change photo' : 'Add a photo'}</span>}
      </button>
      {edit && me.photo && (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" disabled={open.isPending} onClick={() => open.mutate()}>
            <CropIcon aria-hidden /> Rotate, crop and color
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            <Trash2 aria-hidden /> Remove photo
          </Button>
        </div>
      )}
      <FormError error={open.error ?? remove.error} />
      {file && (
        <CropPhoto
          open
          onOpenChange={(o) => !o && setFile(null)}
          source={file}
          crop={null}
          shape="square"
          adding
          onSave={async (crop, copies, size) => {
            const { ref } = await uploadProfilePhoto({ original: file, ...copies, ...size, crop })
            setFile(null)
            await changed(ref ?? null)
          }}
        />
      )}
      {recrop && (
        <CropPhoto
          open
          onOpenChange={(o) => !o && setRecrop(null)}
          source={portraitUrl(`face:${recrop.id}`, 'original')}
          crop={recrop.crop}
          onSave={async (crop, copies) => {
            const { ref } = await saveCrop('/me/photo', crop, copies)
            setRecrop(null)
            await changed(`face:${ref}`)
          }}
        />
      )}
    </div>
  )
}
